import React from 'react';
import { useTransactions } from '@/hooks/use-currency';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { ArrowDownCircle, ArrowUpCircle, Coins, Gem, AlertCircle, History, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

interface TransactionHistoryProps {
  limit?: number;
}

export function TransactionHistory({ limit = 10 }: TransactionHistoryProps) {
  const { data, isLoading, isError } = useTransactions(limit);

  // Data processing is handled in the useTransactions hook
  const transactions = data || [];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.3
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center mb-4">
          <Clock className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold ml-2">Transaction History</h2>
        </div>
        
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border border-gray-800 bg-black/40 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <Skeleton className="h-6 w-20 bg-gray-800" />
                <Skeleton className="h-6 w-24 bg-gray-800" />
              </div>
              <div className="mt-2">
                <Skeleton className="h-4 w-full bg-gray-800" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <Clock className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold ml-2">Transaction History</h2>
        </div>
        
        <div className="rounded-md bg-red-900/20 border border-red-700 p-4 text-red-200 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load transaction history</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-semibold">Transaction History</h2>
        </div>
        <Badge variant="outline" className="bg-primary/10 border-primary/20 text-sm">
          Last {limit} transactions
        </Badge>
      </div>

      {!transactions || transactions.length === 0 ? (
        <div className="text-center p-12 border border-gray-700 rounded-lg bg-gradient-to-br from-black/60 to-gray-900/20">
          <History className="h-12 w-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400 font-medium">No transaction history available</p>
          <p className="text-xs text-gray-500 mt-2">Your transactions will appear here</p>
        </div>
      ) : (
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-3"
        >
          {transactions.map((transaction: any) => (
            <motion.div key={transaction.id} variants={itemVariants}>
              <Card className="border-gray-800 bg-gradient-to-br from-black/80 to-gray-900/10 hover:to-primary/5 transition-all duration-300">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center mb-2">
                    <Badge
                      variant="outline"
                      className={
                        transaction.transactionType === 'purchase' 
                          ? 'bg-red-900/30 text-red-300 border-red-700 px-3 py-0.5' 
                          : transaction.transactionType === 'reward'
                            ? 'bg-green-900/30 text-green-300 border-green-700 px-3 py-0.5'
                            : 'bg-blue-900/30 text-blue-300 border-blue-700 px-3 py-0.5'
                      }
                    >
                      {transaction.transactionType}
                    </Badge>
                    <span className="text-gray-400 text-sm">
                      {format(new Date(transaction.createdAt), 'MMM d, yyyy • h:mm a')}
                    </span>
                  </div>
                  
                  <p className="font-medium text-gray-300 mb-2">{transaction.description}</p>
                  
                  <div className="flex items-center gap-2">
                    <div className={`rounded-full p-1 ${transaction.amount >= 0 ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      {transaction.amount >= 0 ? (
                        <ArrowDownCircle className="h-4 w-4 text-green-400" />
                      ) : (
                        <ArrowUpCircle className="h-4 w-4 text-red-400" />
                      )}
                    </div>
                    <span className={`font-medium ${transaction.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {Math.abs(transaction.amount)}
                    </span>
                    {transaction.currencyType === 'premium' ? (
                      <Gem className="h-4 w-4 text-purple-400" />
                    ) : (
                      <Coins className="h-4 w-4 text-amber-400" />
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}